<script lang="ts" module>
	import type { ISbStoryData, SbBlokData } from '@storyblok/js';
	import type { SbImage, StripeProductData } from '$lib/storyblok/types';

	export interface CartProductAddon extends SbBlokData {
		component: 'cart_product_addon';
		title: string;
		description: string;
		icon?: SbImage;
		addon_product?: StripeProductData;
		base_product?: StripeProductData;
		product_image?: SbImage;
	}
</script>

<script lang="ts">
	import Button from '$lib/buttons/v3/button.svelte';
	import { checkoutSession } from '$lib/state/checkoutSession.svelte';
	import Image from '$lib/storyblok/components/image.svelte';
	import { formatMoney, inDollars } from '$lib/utils/money';
	import log from '$lib/utils/log';
	import { fade } from 'svelte/transition';
	import { trackEvent } from '$lib/segment/client';

	interface Props {
		content: ISbStoryData<CartProductAddon>['content'];
	}

	let { content }: Props = $props();

	let price = $derived(
		checkoutSession.data.addon_unit_amount ||
			(checkoutSession.data.product?.price.unit_amount &&
			content.addon_product?.data.price.unit_amount
				? Math.max(
						0,
						content.addon_product.data.price.unit_amount -
							checkoutSession.data.product.price.unit_amount
					)
				: 0)
	);

	let active = $derived(
		checkoutSession.data.product &&
			content.addon_product &&
			checkoutSession.data.product.price.lookup_key === content.addon_product.priceLookupKey
	);

	let addonAction = $state<'remove' | 'add' | null>(null);

	let selected = $derived(addonAction === 'add' || (addonAction !== 'remove' && active));

	let buttonRef = $state<HTMLButtonElement | HTMLAnchorElement>();

	async function toggle() {
		try {
			if (active) {
				addonAction = 'remove';
				await checkoutSession.restore();
				trackEvent('Product Addon Removed', {
					price_lookup_key: content.addon_product?.priceLookupKey
				});
			} else if (content.addon_product) {
				addonAction = 'add';
				await checkoutSession.select(content.addon_product, { addon_unit_amount: price });
				trackEvent('Product Addon Selected', {
					price_lookup_key: content.addon_product?.priceLookupKey
				});
			} else {
				log.warn('Product addon not set', { content });
			}
			buttonRef?.blur();
		} catch (e) {
			log.error('Cannot toggle addon', {
				error: (e as Error).message,
				active,
				addonAction
			});
		} finally {
			addonAction = null;
		}
	}
</script>

<article
	role={selected ? null : 'button'}
	onclick={selected ? null : toggle}
	class={[
		'order-brown-5 rounded-xl bg-white px-4 pb-4 pt-3 outline',
		{ 'outline-2 outline-brown-1': selected, 'outline-1 outline-brown-5': !selected }
	]}
>
	<div class="mb-[6px] flex items-center gap-x-2">
		{#if content.icon?.filename}
			<Image
				src={content.icon.filename}
				alt={content.icon.alt}
				title={content.icon.title}
				class="h-7 w-7"
			/>
		{/if}
		<h1 class="text-[16px]/[110%] font-semibold tracking-[-.16px] text-brown-1">{content.title}</h1>
	</div>
	<p class="text-[14px]/[160%] text-brown-4">{content.description}</p>
	<hr class="my-4 border-t border-brown-5" />
	<div class="flex items-center justify-between">
		<Button
			bind:buttonRef
			fitContent={false}
			class={['btn-small w-22', { 'btn-secondary btn-brown': selected }]}
			style={selected
				? `outline-color: #E9E7E6; --hover-bg-color: #FAF9F9; --hover-text-color: #240D02`
				: undefined}
			onclick={(e) => {
				e.stopPropagation();
				toggle();
			}}
			disabled={addonAction !== null}>{selected ? 'Remove' : 'Add'}</Button
		>
		{#if price > 0}
			<p
				class={[
					'text-[16px]/[150%] tracking-[-.16px]',
					{ 'text-brown-4': selected, 'text-brown-1': !selected }
				]}
				transition:fade={{ duration: 200 }}
			>
				{formatMoney(inDollars(price))}
			</p>
		{/if}
	</div>
</article>
